"use client";

import { ResponsiveProp } from "@faire/react/viewport/ResponsiveProp";
import { isNotUndefined } from "@faire/typescript";
import { getSpacing, getSpacingBase8 } from "@faire/web/slate/spacing";
import {
  SpacerValueOrKey,
  SpacerValueOrKeyBase8,
} from "@faire/web/slate/spacing/getSpacing";
import React from "react";
import styled from "styled-components";

import { buildResponsiveCss } from "../Theme/ResponsiveCss";

type GapCssBuilder = (
  rule: Parameters<typeof buildResponsiveCss>[0]
) => ReturnType<typeof buildResponsiveCss>;

const getGapCssBuilder = (
  specificBase8: ResponsiveProp<SpacerValueOrKeyBase8> | undefined,
  specific: ResponsiveProp<SpacerValueOrKey> | undefined,
  gapBase8: ResponsiveProp<SpacerValueOrKeyBase8> | undefined,
  gap: ResponsiveProp<SpacerValueOrKey> | undefined
): GapCssBuilder => {
  if (isNotUndefined(specificBase8)) {
    return (rule) => buildResponsiveCss(rule, specificBase8, getSpacingBase8);
  }
  if (isNotUndefined(specific)) {
    return (rule) => buildResponsiveCss(rule, specific, getSpacing);
  }
  if (isNotUndefined(gapBase8)) {
    return (rule) => buildResponsiveCss(rule, gapBase8, getSpacingBase8);
  }
  if (isNotUndefined(gap)) {
    return (rule) => buildResponsiveCss(rule, gap, getSpacing);
  }
  // TODO(alipianu): before removing the base8 props & resolution logic, add prop constraints to IGridProps to force at least one of `gap/gapBase8/rowGap/rowGapBase8/columnGap/columnGapBase8` to be provided
  return (rule) => buildResponsiveCss(rule, "1x", getSpacingBase8);
};

// TODO(alipianu): before removing the base8 props & resolution logic, add prop constraints to IGridProps to force at least one of `gap/gapBase8/rowGap/rowGapBase8/columnGap/columnGapBase8` to be provided
export interface IGridProps {
  /**
   * Defaults to 2 columns.
   */
  columns?: ResponsiveProp<number>;
  /**
   * Defaults to "1x" (4px).
   */
  gap?: ResponsiveProp<SpacerValueOrKey>;
  /**
   * Defaults to "1x" (8px).
   *
   * @deprecated prefer `gap` (base 4) instead of `gapBase8` (base 8) - matching Slate's new dimension tokens base
   */
  gapBase8?: ResponsiveProp<SpacerValueOrKeyBase8>;
  /**
   * Will fallback to `gap` property if not provided.
   */
  rowGap?: ResponsiveProp<SpacerValueOrKey>;
  /**
   * Will fallback to `gapBase8` property if not provided.
   *
   * @deprecated prefer `rowGap` (base 4) instead of `rowGapBase8` (base 8) - matching Slate's new dimension tokens base
   */
  rowGapBase8?: ResponsiveProp<SpacerValueOrKeyBase8>;
  /**
   * Will fallback to `gap` property if not provided.
   */
  columnGap?: ResponsiveProp<SpacerValueOrKey>;
  /**
   * Will fallback to `gapBase8` property if not provided.
   *
   * @deprecated prefer `columnGap` (base 4) instead of `columnGapBase8` (base 8) - matching Slate's new dimension tokens base
   */
  columnGapBase8?: ResponsiveProp<SpacerValueOrKeyBase8>;
  /**
   * Function for producing the `grid-template-columns` value for the
   * given number of columns. Defaults to the example.
   *
   * @example ```jsx
   * (columns: number) => `repeat(${columns}, 1fr);`
   * ```
   */
  columnTemplate?: (columns: number) => string;
  children?: React.ReactNode;
}

const defaultColumnTemplate = (columns: number) => `repeat(${columns}, 1fr);`;

/**
 * @trackcomponent
 */

export const Grid: React.FC<
  IGridProps &
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    > &
    React.RefAttributes<HTMLDivElement>
> = (props) => {
  const {
    children,
    columns,
    gap,
    gapBase8,
    rowGap,
    rowGapBase8,
    columnGap,
    columnGapBase8,
    columnTemplate,
    ...rest
  } = props;

  const rowGapCss = getGapCssBuilder(rowGapBase8, rowGap, gapBase8, gap);
  const columnGapCss = getGapCssBuilder(
    columnGapBase8,
    columnGap,
    gapBase8,
    gap
  );

  return (
    <StyledGrid
      $columns={columns}
      $rowGapCss={rowGapCss}
      $columnGapCss={columnGapCss}
      $columnTemplate={columnTemplate}
      {...rest}
    >
      {children}
    </StyledGrid>
  );
};

const StyledGrid = styled.div<{
  $columns?: ResponsiveProp<number>;
  $rowGapCss: GapCssBuilder;
  $columnGapCss: GapCssBuilder;
  $columnTemplate?: (columns: number) => string;
}>`
  display: grid;
  ${({ $columns = 2, $columnTemplate = defaultColumnTemplate }) =>
    buildResponsiveCss("grid-template-columns", $columns, $columnTemplate)}
  ${({ $rowGapCss }) => $rowGapCss("row-gap")}
  ${({ $columnGapCss }) => $columnGapCss("column-gap")}
  overflow: hidden;
`;
